import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, ArrowRight, FileText, Download } from 'lucide-react';

const sections = [
  {
    title: 'Eligibility Requirements',
    items: [
      'Must hold a valid and current medical/professional registration in their jurisdiction',
      'Must have completed a recognised primary medical/professional qualification',
      'Must meet the specific eligibility criteria for the chosen certification pathway',
      'Must provide verifiable evidence of clinical experience as specified per examination',
      'Must not have any current professional misconduct sanctions that preclude practice',
    ],
  },
  {
    title: 'Application Process',
    items: [
      'Submit online application through the IMCAC Candidate Portal',
      'Provide verified documentation of qualifications and registration',
      'Submit evidence of clinical experience (case logs, portfolio)',
      'Pay applicable examination fees',
      'Receive confirmation of eligibility from the Certification Committee',
      'Schedule examination date at an accredited examination centre',
    ],
  },
  {
    title: 'Examination Rules',
    items: [
      'Candidates must arrive at least 30 minutes before the scheduled examination time',
      'Valid government-issued photo identification is mandatory',
      'No electronic devices are permitted in the examination area',
      'All examination materials remain the property of IMCAC',
      'Candidates must adhere to the IMCAC Code of Conduct throughout the examination',
      'Any form of academic dishonesty will result in immediate disqualification',
    ],
  },
  {
    title: 'Appeals Process',
    items: [
      'Candidates may appeal examination results within 30 days of result notification',
      'Appeals must be submitted in writing with specific grounds for appeal',
      'The Appeals Committee will review all appeals independently',
      'Possible grounds include procedural irregularity, extenuating circumstances, or administrative error',
      'The Appeals Committee decision is communicated within 60 days',
      'A second appeal may be lodged only if new evidence is presented',
    ],
  },
  {
    title: 'Misconduct Policy',
    items: [
      'Any form of cheating, impersonation, or collusion is strictly prohibited',
      'Plagiarism in portfolio submissions will result in disqualification',
      'Candidates found in violation will be reported to their professional regulatory body',
      'Sanctions may include disqualification, suspension, or permanent ban from future examinations',
      'All misconduct allegations are investigated by the Ethics Committee',
    ],
  },
  {
    title: 'Retake Policy',
    items: [
      'Candidates who do not pass may retake the examination after a minimum waiting period',
      'The waiting period is typically 6 months from the date of the failed examination',
      'A maximum of 3 attempts is permitted within a 3-year period',
      'Additional preparation requirements may be specified for subsequent attempts',
      'Retake fees apply for each subsequent examination attempt',
    ],
  },
  {
    title: 'Certification Validity',
    items: [
      'IMCAC certifications are valid for a defined period (typically 5 years)',
      'Certification validity is subject to continued professional registration',
      'Certified professionals must maintain CPD requirements throughout the validity period',
      'Certification status can be verified through the IMCAC Verification Portal',
      'Certificates bear digital signatures and unique verification identifiers',
    ],
  },
  {
    title: 'Recertification Requirements',
    items: [
      'Recertification is required before the expiry of the current certification period',
      'Candidates must demonstrate continued clinical competence through CPD activities',
      'A minimum number of CPD points must be accumulated during the certification period',
      'Recertification may require a condensed examination or portfolio review',
      'Failure to recertify by the expiry date will result in lapsed certification status',
    ],
  },
];

export default function CandidateHandbookPage() {
  return (
    <div data-testid="candidate-handbook-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Candidate Resources</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Candidate Handbook</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            Complete guide to IMCAC examination policies, procedures, and candidate responsibilities.
          </p>
        </div>
      </section>

      {/* Download Banner */}
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-5">
        <div className="imcac-container">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-[#C5A572]" />
              <p className="text-sm text-slate-600">Download the complete Candidate Handbook as a PDF document for offline reference.</p>
            </div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/brochures/candidate-handbook`} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm text-sm" data-testid="download-handbook-btn">
                <Download className="h-4 w-4 mr-2" /> Download PDF
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="space-y-8">
            {sections.map((section, i) => (
              <Card key={i} className="imcac-card" data-testid={`handbook-section-${i}`}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-[#1B2A4A] rounded-sm flex items-center justify-center text-sm font-bold text-[#C5A572]">{i + 1}</span>
                    {section.title}
                  </h3>
                  <div className="space-y-2.5 ml-10">
                    {section.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
